import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, FormControlLabel, Grid, IconButton, Switch, TextField, Typography } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useParams } from 'react-router-dom';
import { addDays } from 'date-fns';
import './utils/css/small.css';
import Centro from '../components/Centro';
import { useServer } from '../context/ServerContext';
import EmailsPorServicio from './utils/EmailsPorServicio';
import { guardarEtiqueta, labelId } from '../features/labels/labels';
import { useLabels } from '../features/labels/LabelsProvider';
import { CrearCentro, EditarCentro, ObtenerCentros } from '../utils/apiServer';

const CreateCenters = ({ translate, editCentro, onEditConsumed }) => {
  const { refresh } = useLabels();
  const superuser = localStorage.getItem("cargo") === "Superusuario";
  const [visibleName, setVisibleName] = useState("");
  const [labelBaseline, setLabelBaseline] = useState("");
  const [persistedCenterId, setPersistedCenterId] = useState(null);
  const [saving, setSaving] = useState(false);
  const t = (key, fallback) => {
    const value = typeof translate === 'function' ? translate(key) : key;
    return value && value !== key ? value : fallback;
  };

  const { servidor } = useServer();
  const { token } = useParams();
  const emailsRef = useRef(null);
  const editBaselineRef = useRef(null);

  const [tipoUbicacion, setTipoUbicacion] = useState('centro');
  const [centros, setCentros] = useState([]);
  const [centroSeleccionado, setCentroSeleccionado] = useState(null);
  const [alertMessage, setAlertMessage] = useState(null);
  const [alertSeverity, setAlertSeverity] = useState('success');
  const [editar, setEditar] = useState(false);
  const [calendarPicker, setCalendarPicker] = useState(null);
  const [pendingDate, setPendingDate] = useState(null);
  const [calendarView, setCalendarView] = useState('days');
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [daysRangeOpen, setDaysRangeOpen] = useState(false);
  const [daysRangeValue, setDaysRangeValue] = useState('39');
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [datesTouched, setDatesTouched] = useState(false);
  const [fieldsTouched, setFieldsTouched] = useState({ specie: false, gps: false });
  const [gpsMode, setGpsMode] = useState('manual');
  const [state, setState] = useState([{ startDate: addDays(new Date(), -7), endDate: new Date(), key: 'selection' }]);
  const [nuevoCentro, setNuevoCentro] = useState({
    enviro: { init: null, fin: null },
    nanoox: { init: null, fin: null },
    pss: { init: null, fin: null },
    scapp: { init: null, fin: null },
    flux: { init: null, fin: null },
  });
  const [centroInfo, setCentroInfo] = useState({ centro: '', area: '', region: '', specie: '', lat: '', lon: '' });
  const [emails, setEmails] = useState({ flux: [], enviro: [], nanoox: [], scapp: [] });

  const calendarPickerRef = useRef(null);

  useEffect(() => {
    const closeCalendarOnOutsideClick = (event) => {
      if (calendarPicker && calendarPickerRef.current && !calendarPickerRef.current.contains(event.target)) {
        setCalendarPicker(null);
        setPendingDate(null);
      }
    };

    document.addEventListener('mousedown', closeCalendarOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeCalendarOnOutsideClick);
  }, [calendarPicker]);

  useEffect(() => {
    if (!Array.isArray(state) || !state[0]?.startDate || !state[0]?.endDate) return;
    const formatDate = (value) => (value instanceof Date ? value : new Date(value)).toISOString().split('T')[0];
    const init = formatDate(state[0].startDate);
    const fin = formatDate(state[0].endDate);
    setNuevoCentro((prev) => ({
      ...prev,
      enviro: { init, fin },
      nanoox: { init, fin },
      pss: { init, fin },
      scapp: { init, fin },
      flux: { init, fin },
    }));
  }, [state]);

  useEffect(() => {
    if (editar) {
      setIsButtonDisabled(false);
      return;
    }
    setIsButtonDisabled(!(centroInfo.centro && centroInfo.area && centroInfo.region));
  }, [centroInfo, editar]);

  useEffect(() => {
    if (tipoUbicacion === 'barco') {
      setCentroInfo((prev) => ({ ...prev, region: 'flux', area: 'barcos' }));
    } else {
      setCentroInfo((prev) => ({ ...prev, region: '', area: '' }));
    }
  }, [tipoUbicacion]);

  const actualizarListaCentros = async () => {
    try {
      const usuariosObtenidos = await ObtenerCentros(servidor, token);
      setCentros(usuariosObtenidos);
    } catch (error) {
      console.error('Error al obtener centros:', error);
    }
  };

  useEffect(() => {
    actualizarListaCentros();
  }, []);

  const handleGpsToggle = () => {
    setGpsMode((prev) => (prev === 'manual' ? 'auto' : 'manual'));
    setFieldsTouched((prev) => ({ ...prev, gps: true }));
  };

  const handleChange = (event) => {
    let { name, value } = event.target;
    value = value.toLowerCase().trim();
    setCentroInfo((prev) => ({ ...prev, [name]: value }));
    if (name === 'specie') setFieldsTouched((prev) => ({ ...prev, specie: true }));
    if (name === 'lat' || name === 'lon') setFieldsTouched((prev) => ({ ...prev, gps: true }));
  };

  const resetForm = () => {
    setVisibleName(""); setLabelBaseline(""); setPersistedCenterId(null);
    setCentroInfo({ centro: '', area: '', region: '', specie: '', lat: '', lon: '' });
    setNuevoCentro({
      enviro: { init: null, fin: null },
      nanoox: { init: null, fin: null },
      pss: { init: null, fin: null },
      scapp: { init: null, fin: null },
      flux: { init: null, fin: null },
    });
    setState([{ startDate: addDays(new Date(), -7), endDate: new Date(), key: 'selection' }]);
  };

  const handleSubmit = async (event) => {
    try {
      event.preventDefault();
      const centro_id = `${centroInfo.centro}-${centroInfo.area}-${centroInfo.region}`;
      emailsRef.current?.commitPendingEmail();
      const gps_coordinates = {
        active: gpsMode,
        ...(gpsMode === 'manual' && {
          manual: { lat: parseFloat(centroInfo.lat), lon: parseFloat(centroInfo.lon) },
        }),
      };
      const newCentro = {
        centro_id,
        cicle_info: { specie: centroInfo.specie },
        gps_coordinates,
        ...nuevoCentro,
        emails,
      };
      setSaving(true);
      if (!persistedCenterId) {
        await CrearCentro([newCentro], setAlertMessage, setAlertSeverity, translate, servidor, token);
        setPersistedCenterId(centro_id);
        setCentros((prev) => [...prev, newCentro]);
      }
      if (superuser && visibleName.trim()) {
        try {
          await guardarEtiqueta(servidor, 'centro', persistedCenterId || centro_id, visibleName);
          await refresh();
        } catch (error) {
          setAlertMessage(t('centros_cycle_saved_label_failed', 'Ciclo guardado. Reintenta guardar el nombre visible.'));
          setAlertSeverity('error');
          return;
        }
      }
      setAlertMessage(t("centros_saved", "Cambios guardados."));
      setAlertSeverity("success");
      resetForm();
      await actualizarListaCentros();
    } catch (error) {
      console.error('Error al crear centro:', error);
    } finally { setSaving(false); }
  };

  const handleEdit = (centro) => {
    setEditar(true);
    setCentroSeleccionado(centro.centro_id);
    const name = labelId(centro.centro_id, 'car', '');
    setVisibleName(name); setLabelBaseline(name); setPersistedCenterId(null);
    const specie = centro?.cicle_info?.specie || '';
    const activeMode = centro?.gps_coordinates?.active === 'manual' ? 'manual' : 'auto';
    const manualLat = centro?.gps_coordinates?.manual?.lat ?? '';
    const manualLon = centro?.gps_coordinates?.manual?.lon ?? '';
    const getCycle = (service) => ({
      init: centro?.[service]?.init ?? null,
      fin: centro?.[service]?.fin ?? null,
    });
    const cycles = {
      enviro: getCycle('enviro'),
      nanoox: getCycle('nanoox'),
      pss: getCycle('pss'),
      scapp: getCycle('scapp'),
      flux: getCycle('flux'),
    };
    const referenceCycle = Object.values(cycles).find(({ init, fin }) => init && fin);
    setCentroInfo((prev) => ({ ...prev, specie, lat: manualLat, lon: manualLon }));
    setGpsMode(activeMode);
    setNuevoCentro(cycles);
    const calendarDate = (value) => {
      const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00:00` : value);
      return Number.isNaN(date.getTime()) ? new Date() : date;
    };
    setState([{
      startDate: referenceCycle ? calendarDate(referenceCycle.init) : new Date(),
      endDate: referenceCycle ? calendarDate(referenceCycle.fin) : new Date(),
      key: 'selection',
    }]);
    editBaselineRef.current = {
      specie,
      gps: { active: activeMode, manual: { lat: manualLat, lon: manualLon } },
      dates: cycles,
    };
    setDatesTouched(false);
    setFieldsTouched({ specie: false, gps: false });
  };

  useEffect(() => {
    if (editCentro) {
      handleEdit(editCentro);
      onEditConsumed();
    }
  }, [editCentro]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConfirmEdit = async (event) => {
    try {
      event.preventDefault();
      const patch = {};
      const baseline = editBaselineRef.current || {};
      if (datesTouched) {
        patch.enviro = { ...nuevoCentro.enviro };
        patch.nanoox = { ...nuevoCentro.nanoox };
        patch.pss = { ...nuevoCentro.pss };
        patch.scapp = { ...nuevoCentro.scapp };
        patch.flux = { ...nuevoCentro.flux };
      }
      if (fieldsTouched.specie && centroInfo.specie !== baseline.specie) patch.cicle_info = { specie: centroInfo.specie };
      const nextGps = {
        active: gpsMode,
        ...(gpsMode === 'manual'
          ? { manual: { lat: parseFloat(centroInfo.lat), lon: parseFloat(centroInfo.lon) } }
          : {}),
      };
      const sameMode = nextGps.active === baseline?.gps?.active;
      const sameManual =
        (nextGps.active !== 'manual' && baseline?.gps?.active !== 'manual') ||
        (nextGps.active === 'manual' &&
          baseline?.gps?.active === 'manual' &&
          Number(nextGps.manual?.lat) === Number(baseline.gps?.manual?.lat) &&
          Number(nextGps.manual?.lon) === Number(baseline.gps?.manual?.lon));
      if (fieldsTouched.gps && !(sameMode && sameManual)) patch.gps_coordinates = nextGps;
      if (Object.keys(patch).length === 0 && visibleName === labelBaseline) {
        setAlertMessage(translate?.('no_hay_cambios') || 'No hay cambios para guardar.');
        setAlertSeverity('info');
        return;
      }
      setSaving(true);
      if (Object.keys(patch).length) {
        await EditarCentro(patch, setAlertMessage, setAlertSeverity, translate, centroSeleccionado, servidor, token);
        editBaselineRef.current = { specie: centroInfo.specie, gps: nextGps, dates: nuevoCentro };
        setDatesTouched(false); setFieldsTouched({ specie: false, gps: false });
      }
      if (superuser && visibleName !== labelBaseline) {
        await guardarEtiqueta(servidor, 'centro', centroSeleccionado, visibleName);
        await refresh();
      }
      setAlertMessage(t("centros_saved", "Cambios guardados."));
      setAlertSeverity("success");
      resetForm();
      setEditar(false);
      await actualizarListaCentros();
    } catch (error) {
      console.error('Error al actualizar centro:', error);
      setAlertMessage(t('centros_save_error', 'No se pudo guardar. Reintenta.'));
      setAlertSeverity('error');
    } finally { setSaving(false); }
  };

  const cancelEdit = () => {
    resetForm();
    setEditar(false);
    editBaselineRef.current = null;
    setDatesTouched(false);
    setFieldsTouched({ specie: false, gps: false });
  };

  const capitalizeCenter = (value) => {
    if (!value) return '';
    const trimmedStr = value.split('-')[0].trim();
    return trimmedStr.charAt(0).toUpperCase() + trimmedStr.slice(1);
  };

  const startPreview = state?.[0]?.startDate ? new Date(state[0].startDate) : null;
  const endPreview = state?.[0]?.endDate ? new Date(state[0].endDate) : null;
  const rangeStart = startPreview && endPreview && startPreview > endPreview ? endPreview : startPreview;
  const rangeEnd = startPreview && endPreview && startPreview > endPreview ? startPreview : endPreview;
  const rangeDays = rangeStart && rangeEnd ? Math.floor((rangeEnd - rangeStart) / 86400000) + 1 : 0;
  const rangeLabel =
    rangeStart && rangeEnd
      ? `${rangeStart.toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' })} - ${rangeEnd.toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' })}`
      : t('no_range_selected', 'Sin rango seleccionado');

  const formatRangeDate = (date) =>
    date
      ? date.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' })
      : t('select_date', 'Seleccionar fecha');

  const atStartOfDay = (value) => {
    const date = new Date(value);
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  };

  const applyQuickRange = (startDate, endDate) => {
    setDatesTouched(true);
    setState([{ startDate: atStartOfDay(startDate), endDate: atStartOfDay(endDate), key: 'selection' }]);
    setCalendarPicker(null);
    setPendingDate(null);
    setDaysRangeOpen(false);
  };

  const openCalendar = (picker) => {
    const currentDate = picker === 'start' ? rangeStart : rangeEnd;
    const baseDate = currentDate || rangeStart || new Date();
    setCalendarPicker(picker);
    setPendingDate(currentDate ? atStartOfDay(currentDate) : null);
    setCalendarMonth(baseDate.getMonth());
    setCalendarYear(baseDate.getFullYear());
    setCalendarView('days');
    setDaysRangeOpen(false);
  };

  const cancelCalendarSelection = () => {
    setCalendarPicker(null);
    setPendingDate(null);
  };

  const applyCalendarSelection = () => {
    if (!pendingDate) return;

    setDatesTouched(true);
    if (calendarPicker === 'start') {
      const nextEnd = rangeEnd && rangeEnd >= pendingDate ? rangeEnd : null;
      setState([{ startDate: pendingDate, endDate: nextEnd, key: 'selection' }]);
      setCalendarPicker('end');
      setPendingDate(nextEnd ? atStartOfDay(nextEnd) : null);
      if (nextEnd) {
        setCalendarMonth(nextEnd.getMonth());
        setCalendarYear(nextEnd.getFullYear());
      }
      return;
    }

    if (rangeStart) {
      setState([{ startDate: rangeStart, endDate: pendingDate, key: 'selection' }]);
    }
    cancelCalendarSelection();
  };

  const navigateCalendar = (direction) => {
    const next = new Date(calendarYear, calendarMonth + direction, 1);
    setCalendarMonth(next.getMonth());
    setCalendarYear(next.getFullYear());
  };

  const applyCurrentWeek = () => {
    const today = new Date();
    const mondayOffset = (today.getDay() + 6) % 7;
    const monday = addDays(today, -mondayOffset);
    applyQuickRange(monday, addDays(monday, 6));
  };

  const applyCurrentMonth = () => {
    const today = new Date();
    applyQuickRange(new Date(today.getFullYear(), today.getMonth(), 1), new Date(today.getFullYear(), today.getMonth() + 1, 0));
  };

  const applyYearFromToday = () => {
    const today = atStartOfDay(new Date());
    applyQuickRange(today, new Date(today.getFullYear() + 1, today.getMonth(), today.getDate()));
  };

  const applyFullYear = () => {
    const year = new Date().getFullYear();
    applyQuickRange(new Date(year, 0, 1), new Date(year, 11, 31));
  };

  const applyDaysFromToday = () => {
    const days = Number(daysRangeValue);
    if (!Number.isInteger(days) || days < 1 || days > 730) return;
    const today = atStartOfDay(new Date());
    applyQuickRange(today, addDays(today, days - 1));
  };

  const calendarMonths = [
    t('month_short_jan', 'Ene'), t('month_short_feb', 'Feb'), t('month_short_mar', 'Mar'),
    t('month_short_apr', 'Abr'), t('month_short_may', 'May'), t('month_short_jun', 'Jun'),
    t('month_short_jul', 'Jul'), t('month_short_aug', 'Ago'), t('month_short_sep', 'Sep'),
    t('month_short_oct', 'Oct'), t('month_short_nov', 'Nov'), t('month_short_dec', 'Dic'),
  ];
  const calendarWeekdays = [
    t('weekday_mon', 'Lun'), t('weekday_tue', 'Mar'), t('weekday_wed', 'Mie'),
    t('weekday_thu', 'Jue'), t('weekday_fri', 'Vie'), t('weekday_sat', 'Sab'), t('weekday_sun', 'Dom'),
  ];
  const firstCalendarDay = new Date(calendarYear, calendarMonth, 1);
  const calendarOffset = (firstCalendarDay.getDay() + 6) % 7;
  const calendarDays = Array.from({ length: 42 }, (_, index) => new Date(calendarYear, calendarMonth, 1 - calendarOffset + index));
  const currentCalendarYear = new Date().getFullYear();
  const calendarYears = Array.from({ length: 12 }, (_, index) => currentCalendarYear - 2 + index);

  return (
    <Box sx={{ height: '100%', minHeight: 0, overflowY: 'auto', pr: '0.2vw' }}>
      <Grid container justifyContent="center" sx={{ cursor: 'default', userSelect: 'none', minHeight: '90vh', gap: '1vw' }}>
        <Grid item xs={12}>
          <Box sx={heroCardSx}>
            <Typography sx={heroKickerSx}>{t('configuration_access', 'Configuration')}</Typography>
            <Typography sx={heroTitleSx}>
              {!editar ? t('create_center', 'Definir ciclo') : `${t('edit_ciclo', 'Editar ciclo')} ${capitalizeCenter(String(centroSeleccionado))}`}
            </Typography>
            <Typography sx={heroTextSx}>{t('center_management_description', 'Define ciclos, ventanas de servicio, coordenadas y notificaciones desde una vista mas clara y ordenada.')}</Typography>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box sx={twoColumnLayoutSx}>
            <Box sx={leftColumnStackSx}>
              <Box sx={rangeCardSx}>
                <Box sx={rangeHeaderSx}>
                  <Box>
                    <Typography sx={stepKickerSx}>{t('temporal_range', 'Rango temporal')}</Typography>
                    <Typography sx={rangeSummaryTitleSx}>{t('cycle_range', 'Rango del ciclo')}</Typography>
                  </Box>
                  <Box sx={daysPillSx}>
                    {rangeDays} {rangeDays === 1 ? t('day', 'dia') : t('days', 'dias')}
                  </Box>
                </Box>

                <Box sx={dateFieldsSx}>
                  <Box sx={dateFieldGroupSx}>
                    <Typography sx={dateFieldLabelSx}>{t('start_date', 'Fecha de inicio')}</Typography>
                    <Button aria-label={t('start_date', 'Fecha de inicio')} onClick={() => openCalendar('start')} sx={dateFieldButtonSx(calendarPicker === 'start')} endIcon={<CalendarMonthIcon />}>
                      {formatRangeDate(rangeStart)}
                    </Button>
                  </Box>
                  <Box sx={dateFieldGroupSx}>
                    <Typography sx={dateFieldLabelSx}>{t('end_date', 'Fecha de termino')}</Typography>
                    <Button aria-label={t('end_date', 'Fecha de termino')} onClick={() => openCalendar('end')} sx={dateFieldButtonSx(calendarPicker === 'end')} endIcon={<CalendarMonthIcon />}>
                      {formatRangeDate(rangeEnd)}
                    </Button>
                  </Box>

                  {calendarPicker && (
                    <Box ref={calendarPickerRef} sx={calendarPopoverSx(calendarPicker)}>
                      <Box sx={calendarPopoverHeaderSx}>
                        <IconButton onClick={() => navigateCalendar(-1)} sx={calendarNavButtonSx} aria-label={t('previous_month', 'Mes anterior')}>
                          <KeyboardArrowLeftIcon />
                        </IconButton>
                        <Button onClick={() => setCalendarView((view) => (view === 'days' ? 'years' : 'days'))} sx={calendarTitleButtonSx}>
                          {calendarMonths[calendarMonth]} {calendarYear}
                        </Button>
                        <IconButton onClick={() => navigateCalendar(1)} sx={calendarNavButtonSx} aria-label={t('next_month', 'Mes siguiente')}>
                          <KeyboardArrowRightIcon />
                        </IconButton>
                      </Box>

                      {calendarView === 'years' && (
                        <>
                          <Typography sx={calendarPickerLabelSx}>{t('select_year', 'Selecciona el año')}</Typography>
                          <Box sx={calendarOptionGridSx}>
                            {calendarYears.map((year) => (
                              <Button
                                key={year}
                                onClick={() => {
                                  setCalendarYear(year);
                                  setCalendarView('months');
                                }}
                                sx={calendarOptionSx(year === calendarYear)}
                              >
                                {year}
                              </Button>
                            ))}
                          </Box>
                        </>
                      )}

                      {calendarView === 'months' && (
                        <>
                          <Typography sx={calendarPickerLabelSx}>{t('select_month', 'Selecciona el mes')} · {calendarYear}</Typography>
                          <Box sx={calendarOptionGridSx}>
                            {calendarMonths.map((month, index) => (
                              <Button
                                key={month}
                                onClick={() => {
                                  setCalendarMonth(index);
                                  setCalendarView('days');
                                }}
                                sx={calendarOptionSx(index === calendarMonth)}
                              >
                                {month}
                              </Button>
                            ))}
                          </Box>
                        </>
                      )}

                      {calendarView === 'days' && (
                        <>
                          <Box sx={calendarWeekdaysSx}>
                            {calendarWeekdays.map((weekday) => <Typography key={weekday}>{weekday}</Typography>)}
                          </Box>
                          <Box sx={calendarDaysGridSx}>
                            {calendarDays.map((day) => {
                              const dayTime = day.getTime();
                              const isCurrentMonth = day.getMonth() === calendarMonth;
                              const isDisabled = calendarPicker === 'end' && rangeStart && dayTime < atStartOfDay(rangeStart).getTime();
                              const isSelected = pendingDate && dayTime === pendingDate.getTime();
                              const isAnchor = calendarPicker === 'end' && rangeStart && dayTime === atStartOfDay(rangeStart).getTime();
                              const isInRange = calendarPicker === 'end' && rangeStart && pendingDate && dayTime > atStartOfDay(rangeStart).getTime() && dayTime < pendingDate.getTime();
                              const isToday = dayTime === atStartOfDay(new Date()).getTime();

                              return (
                                <Button
                                  key={day.toISOString()}
                                  disabled={Boolean(isDisabled)}
                                  onClick={() => setPendingDate(day)}
                                  sx={calendarDaySx({ isCurrentMonth, isSelected, isAnchor, isInRange, isToday })}
                                >
                                  {day.getDate()}
                                </Button>
                              );
                            })}
                          </Box>
                        </>
                      )}

                      <Box sx={calendarFooterSx}>
                        <Typography sx={calendarHintSx}>
                          {calendarPicker === 'start' ? t('select_start_date', 'Selecciona la fecha de inicio') : t('select_end_date', 'Selecciona la fecha de termino')}
                        </Typography>
                        <Box sx={calendarFooterActionsSx}>
                          <Button onClick={cancelCalendarSelection} sx={calendarCancelButtonSx}>{t('cancel_button', 'Cancelar')}</Button>
                          <Button disabled={!pendingDate} onClick={applyCalendarSelection} sx={calendarApplyButtonSx}>{t('apply_range', 'Aplicar rango')}</Button>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>

                <Typography sx={rangeSummaryTextSx}>{rangeLabel}</Typography>

                <Box sx={quickRangeAreaSx}>
                  <Typography sx={quickRangeLabelSx}>{t('quick_selection', 'Seleccion rapida')}</Typography>
                  <Box sx={quickRangeButtonsSx}>
                    <Button onClick={applyYearFromToday} sx={quickRangeButtonSx}>{t('one_year_from_today', 'Un año desde hoy')}</Button>
                    <Button onClick={applyFullYear} sx={quickRangeButtonSx}>{t('full_current_year', 'Este año completo')}</Button>
                    <Button onClick={() => { setDaysRangeOpen((open) => !open); cancelCalendarSelection(); }} sx={quickRangeButtonSx}>{t('days_from_today', 'Rango de días desde hoy')}</Button>
                    <Button onClick={applyCurrentWeek} sx={quickRangeButtonSx}>{t('current_week', 'Esta semana')}</Button>
                    <Button onClick={applyCurrentMonth} sx={quickRangeButtonSx}>{t('current_month', 'Este mes')}</Button>
                  </Box>
                  {daysRangeOpen && (
                    <Box sx={daysRangeEditorSx}>
                      <Typography>{t('days_from_today_label', 'Días desde hoy')}</Typography>
                      <TextField
                        type="number"
                        value={daysRangeValue}
                        onChange={(event) => setDaysRangeValue(event.target.value)}
                        inputProps={{ min: 1, max: 730 }}
                        sx={daysRangeInputSx}
                      />
                      <Typography>{t('days', 'dias')}</Typography>
                      <Button onClick={applyDaysFromToday} sx={daysRangeApplySx}>{t('apply_button', 'Aplicar')}</Button>
                    </Box>
                  )}
                </Box>

                <Box sx={rangeHelpSx}>
                  <Box sx={rangeHelpDotSx} />
                  <Typography sx={rangeHelpTextSx}>{t('cycle_range_help', 'Define el rango del ciclo para configurar correctamente sus servicios.')}</Typography>
                </Box>
              </Box>

              <Box sx={sectionCardSx}>
                <Typography sx={sectionKickerSx}>{t('notification_channels', 'Notificaciones')}</Typography>
                <Typography sx={sectionTitleSx}>{t('notification_emails', 'Correos por servicio')}</Typography>
                <Typography sx={sectionTextSx}>{t('notification_emails_description', 'Administra los correos que recibiran alertas y eventos operativos por cada servicio.')}</Typography>
                <Box sx={{ mt: '0.7vw' }}>
                  <EmailsPorServicio ref={emailsRef} emails={emails} setEmails={setEmails} textfieldStyle={textfieldStyle} />
                </Box>
              </Box>
            </Box>

            <Box sx={stepSectionCardSx}>
              <Box sx={headerRowCompactSx}>
                <Box>
                  <Typography sx={sectionHeadingSx}>
                    {!editar ? t('location_data_title', 'Completa la informacion de la ubicacion') : t('edit_center_data_title', 'Ajusta los datos del ciclo seleccionado')}
                  </Typography>
                  {/* <Typography sx={sectionBodyCompactSx}>
                    {!editar
                      ? t('location_data_description', 'Primero define si crearas un centro o un barco. Luego completa la informacion base y la configuracion de coordenadas.')
                      : t('edit_center_data_description', 'Modifica especie, modo de coordenadas y ubicacion manual sin perder el contexto del ciclo activo.')}
                  </Typography> */}
                </Box>
                {/* <Box sx={{ display: 'flex', gap: '0.5vw', flexWrap: 'wrap' }}>
                  {!editar && <Box sx={pillSx(tipoUbicacion === 'centro')}>{tipoUbicacion === 'centro' ? t('center', 'Centro') : t('boat', 'Barco')}</Box>}
                  {editar && centroSeleccionado && <Box sx={pillSx(true)}>{capitalizeCenter(String(centroSeleccionado))}</Box>}
                </Box> */}
              </Box>

              <Box sx={configFormLayoutSx}>
                <Box sx={formBlockSx}>
                  <Typography sx={blockTitleSx}>{!editar ? t('location_identity', 'Identidad de la ubicacion') : t('cycle_summary', 'Resumen del ciclo')}</Typography>
                  <Typography sx={blockTextSx}>
                    {!editar ? t('location_identity_description', 'Usa estos campos para identificar la ubicacion de forma clara y evitar errores en la creacion.') : t('cycle_summary_description', 'Aqui puedes ajustar los datos operativos que cambian con mas frecuencia.')}
                  </Typography>

                  {!editar && (
                    <Box sx={toggleBoxSx}>
                      <Typography sx={toggleLegendSx}>{t('location_type', 'Tipo de ubicacion')}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.45vw' }}>
                        <Typography sx={toggleLabelSx(tipoUbicacion === 'centro')}>{t('center', 'Centro')}</Typography>
                        <Switch disabled={!!persistedCenterId || saving} checked={tipoUbicacion === 'barco'} onChange={() => setTipoUbicacion((prev) => (prev === 'centro' ? 'barco' : 'centro'))} sx={compactSwitchSx} />
                        <Typography sx={toggleLabelSx(tipoUbicacion === 'barco')}>{t('boat', 'Barco')}</Typography>
                      </Box>
                    </Box>
                  )}

                  {!editar && tipoUbicacion === 'barco' && (
                    <Box sx={noticeBoxSx}>
                      <Typography sx={noticeTitleSx}>{t('boat_defaults', 'Valores automaticos para barcos')}</Typography>
                      <Typography sx={noticeTextSx}>{t('boat_defaults_description', 'Al crear un barco se completa area = barcos y region = flux automaticamente.')}</Typography>
                    </Box>
                  )}

                  {editar && centroSeleccionado && (
                    <Box sx={noticeBoxSx}>
                      <Typography sx={noticeTitleSx}>{t('editing_center', 'Ubicacion seleccionada')}</Typography>
                      <Typography sx={noticeTextSx}>{labelId(centroSeleccionado, "car", capitalizeCenter(String(centroSeleccionado)))}</Typography>
                    </Box>
                  )}

                  <Box sx={compactFieldsGridSx}>
                    {superuser && <TextField label={t("centros_visible_name", "Nombre visible")} value={visibleName} onChange={(e) => setVisibleName(e.target.value)} inputProps={{ maxLength: 60 }} helperText={t("centros_name_help", "Vacío conserva el nombre actual.")} fullWidth sx={{ ...textfieldStyle, gridColumn: "1 / -1" }} disabled={saving} />}
                    {!editar && tipoUbicacion === 'centro' && (
                      <>
                        <TextField disabled={!!persistedCenterId} name="region" placeholder={translate('region')} value={centroInfo.region} onChange={handleChange} fullWidth label={translate('region')} InputLabelProps={{ shrink: true }} sx={textfieldStyle} />
                        <TextField disabled={!!persistedCenterId} name="area" placeholder={translate('area')} value={centroInfo.area} onChange={handleChange} fullWidth label={translate('area')} InputLabelProps={{ shrink: true }} sx={textfieldStyle} />
                      </>
                    )}
                    {!editar && (
                      <TextField disabled={!!persistedCenterId} placeholder={tipoUbicacion === 'centro' ? translate('centro') : 'Barco'} name="centro" value={centroInfo.centro} onChange={handleChange} fullWidth label={tipoUbicacion === 'centro' ? translate('centro') : 'Barco'} InputLabelProps={{ shrink: true }} sx={textfieldStyle} />
                    )}
                    <TextField name="specie" placeholder={translate('Especie')} value={centroInfo.specie} onChange={handleChange} fullWidth label={translate('Especie')} InputLabelProps={{ shrink: true }} sx={textfieldStyle} />
                  </Box>
                </Box>

                <Box sx={formBlockSx}>
                  <Typography sx={blockTitleSx}>{t('coordinates', 'Coordenadas')}</Typography>
                  <Typography sx={blockTextSx}>{t('coordinates_description', 'Elige si la posicion se gestionara automaticamente o si quieres ingresar latitud y longitud manualmente.')}</Typography>
                  <Box sx={toggleBoxSx}>
                    <Typography sx={toggleLegendSx}>{t('gps_mode', 'Modo GPS')}</Typography>
                    <FormControlLabel
                      control={<Switch checked={gpsMode === 'manual'} onChange={handleGpsToggle} sx={compactSwitchSx} />}
                      sx={formControlLabelSx}
                      label={gpsMode === 'manual' ? 'Manual' : 'Auto'}
                    />
                  </Box>
                  {gpsMode === 'manual' ? (
                    <Box sx={compactFieldsGridSx}>
                      <TextField name="lat" placeholder={translate('Latitud')} value={centroInfo.lat} onChange={handleChange} fullWidth label={translate('Latitud')} InputLabelProps={{ shrink: true }} sx={textfieldStyle} />
                      <TextField name="lon" placeholder={translate('Longitud')} value={centroInfo.lon} onChange={handleChange} fullWidth label={translate('Longitud')} InputLabelProps={{ shrink: true }} sx={textfieldStyle} />
                    </Box>
                  ) : (
                    <Box sx={noticeBoxSx}>
                      <Typography sx={noticeTitleSx}>{t('automatic_coordinates', 'Coordenadas automaticas activas')}</Typography>
                      <Typography sx={noticeTextSx}>{t('automatic_coordinates_description', 'No necesitas ingresar latitud ni longitud manualmente mientras el modo automatico este activo.')}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button disabled={isButtonDisabled || saving} onClick={!editar ? handleSubmit : handleConfirmEdit} variant="contained" sx={primaryActionSx}>
              {editar ? translate('editar_centro') : translate('crear_centro')}
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12}>
          {/* <Box sx={sectionCardSx}> */}
            {/* <Typography sx={sectionKickerSx}>{t('registered_locations', 'Registro actual')}</Typography>
            <Typography sx={sectionTitleSx}>{t('created_centers', 'Centros y barcos creados')}</Typography>
            <Typography sx={sectionTextSx}>{t('created_centers_description', 'Revisa la configuracion existente, selecciona un elemento y vuelve a editarlo cuando sea necesario.')}</Typography>
            <Box sx={{ mt: '1vw' }}> */}
              <Centro servidor={servidor} token={token} editar={editar} cancelEdit={cancelEdit} translate={translate} centros={centros} actualizarListaCentros={actualizarListaCentros} centroSeleccionado={centroSeleccionado} setCentroSeleccionado={setCentroSeleccionado} handleEdit={handleEdit} />
            {/* </Box> */}
          {/* </Box> */}
        </Grid>

        {alertMessage && (
          <Alert severity={alertSeverity} onClose={() => setAlertMessage(null)} style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 9999, fontSize: '2vh' }}>
            {alertMessage}
          </Alert>
        )}
      </Grid>
    </Box>
  );
};

export default CreateCenters;

const textfieldStyle = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    minHeight: '42px',
    borderRadius: '11px',
    backgroundColor: 'rgba(7,14,25,.5)',
    '& fieldset': { borderColor: 'rgba(233, 78, 26, .24)', borderWidth: '1px' },
    '&:hover fieldset': { borderColor: 'rgba(233, 78, 26, .55)' },
    '&.Mui-focused fieldset': { borderColor: 'rgba(233, 78, 26, .85)' },
    '& .MuiInputBase-input': { color: '#d8e0ea', fontSize: '13.5px', padding: '10px 14px' },
  },
  '& .MuiInputLabel-root': { color: '#aeb9c6', fontSize: '12px' },
  '& .MuiInputLabel-shrink': { fontSize: '12px' },
};

const heroCardSx = {
  border: '1px solid rgba(233, 78, 26, 0.24)',
  borderRadius: '18px',
  px: { xs: '20px', md: '30px' },
  py: { xs: '18px', md: '24px' },
  background: 'radial-gradient(circle at top right, rgba(233, 78, 26, 0.12), transparent 28%), linear-gradient(180deg, rgba(15, 23, 38, 0.96), rgba(8, 14, 24, 0.96))',
  boxShadow: 'inset 0 0 0 0.01vw rgba(255,255,255,0.03)',
  position: 'relative',
  overflow: 'hidden',
  '&::after': { content: '""', position: 'absolute', top: 0, right: 0, bottom: 0, width: '5px', background: '#e94e1a' },
};

const sectionCardSx = {
  p: { xs: '18px', md: '22px 26px' },
  border: '1px solid rgba(233, 78, 26, 0.24)',
  borderRadius: '18px',
  background: 'radial-gradient(circle at top right, rgba(233, 78, 26, 0.08), transparent 26%), linear-gradient(180deg, rgba(15, 23, 38, 0.96), rgba(8, 14, 24, 0.96))',
  boxShadow: 'inset 0 0 0 0.01vw rgba(255,255,255,0.03)',
  boxSizing: 'border-box',
};
const stepSectionCardSx = {
  ...sectionCardSx,
  p: { xs: '18px', md: '22px 26px' },
  display: 'flex',
  flexDirection: 'column',
};

const heroKickerSx = { fontSize: '11px', color: '#e94e1a', fontWeight: 800, letterSpacing: '2px', textTransform: 'uppercase' };
const heroTitleSx = { mt: '4px', fontSize: { xs: '24px', md: '28px' }, fontWeight: 700, color: '#f3f5f8' };
const heroTextSx = { mt: '6px', fontSize: '14px', color: '#97a6b7', lineHeight: 1.5 };
const sectionKickerSx = { fontSize: '11px', color: '#e94e1a', fontWeight: 800, letterSpacing: '2px', textTransform: 'uppercase', textAlign: 'center' };
const sectionTitleSx = { mt: '3px', fontSize: '20px', fontWeight: 700, color: '#f3f5f8', textAlign: 'center' };
const sectionTextSx = { mt: '6px', fontSize: '13px', color: '#97a6b7', textAlign: 'center', lineHeight: 1.5 };
const headerRowSx = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1vw', flexWrap: 'wrap' };
const stepKickerSx = { fontSize: '11px', color: '#e94e1a', fontWeight: 800, letterSpacing: '2px', textTransform: 'uppercase' };
const sectionHeadingSx = { mt: '2px', fontSize: '20px', fontWeight: 700, color: '#f4f7fb', lineHeight: 1.15 };
// const sectionBodySx = { mt: '0.3vh', fontSize: '0.64vw', color: '#98a8ba', lineHeight: 1.45, maxWidth: '44vw' };
// const sectionBodyCompactSx = { ...sectionBodySx, maxWidth: '100%' };
// const summaryCardSx = { minWidth: '12vw', maxWidth: '16vw', borderRadius: '1vw', border: '0.01vw solid rgba(233, 78, 26, 0.2)', background: 'rgba(255,255,255,0.03)', px: '0.9vw', py: '0.9vh' };
// const summaryCardCompactSx = { ...summaryCardSx, minWidth: '10.2vw', maxWidth: '12.8vw', px: '0.75vw', py: '0.65vh' };
// const summaryValueSx = { mt: '0.2vh', fontSize: '0.66vw', fontWeight: 700, color: '#f5f7fb', lineHeight: 1.3 };
// const summaryMetaSx = { mt: '0.18vh', fontSize: '0.58vw', color: '#ffb28e', fontWeight: 700 };
// const pillSx = (active) => ({ borderRadius: '999px', border: `0.01vw solid ${active ? 'rgba(233,78,26,0.5)' : 'rgba(255,255,255,0.12)'}`, background: active ? 'rgba(233,78,26,0.14)' : 'rgba(255,255,255,0.04)', color: active ? '#fff0e8' : '#9eb0c3', px: '0.85vw', py: '0.55vh', fontSize: '0.68vw', fontWeight: 700 });
const twoColumnLayoutSx = { display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(430px, 1fr))' }, gap: '22px', alignItems: 'start' };
const leftColumnStackSx = { display: 'grid', gap: '22px', minWidth: 0 };
const rangeCardSx = {
  p: { xs: '18px', md: '22px 26px' },
  border: '1px solid rgba(233, 78, 26, 0.24)',
  borderRadius: '18px',
  background: 'radial-gradient(circle at top right, rgba(233, 78, 26, 0.12), transparent 30%), linear-gradient(180deg, rgba(15, 23, 38, 0.96), rgba(8, 14, 24, 0.96))',
};
const rangeHeaderSx = { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' };
const rangeSummaryTitleSx = { mt: '3px', fontSize: '20px', fontWeight: 700, color: '#f4f7fb', lineHeight: 1.1 };
const rangeSummaryTextSx = { mt: '12px', fontSize: '13px', color: '#b8c5d4', lineHeight: 1.35 };
const daysPillSx = {
  flex: 'none', borderRadius: '999px', border: '1px solid rgba(233,78,26,.4)', background: 'rgba(233,78,26,.1)',
  color: '#ffb184', px: '14px', py: '6px', fontSize: '12.5px', fontWeight: 700,
};
const dateFieldsSx = { display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: '16px', mt: '18px', position: 'relative' };
const dateFieldGroupSx = { minWidth: 0 };
const dateFieldLabelSx = { fontSize: '12px', color: '#9aaabf', mb: '6px' };
const dateFieldButtonSx = (active) => ({
  width: '100%', minHeight: '44px', justifyContent: 'space-between', textTransform: 'none', borderRadius: '11px',
  border: `1px solid ${active ? '#e94e1a' : 'rgba(233,78,26,.24)'}`, background: 'rgba(7,14,25,.5)', color: '#dfe7f1', px: '14px', fontSize: '14px', fontWeight: 500,
  '&:hover': { borderColor: '#e94e1a', background: 'rgba(233,78,26,.08)' },
  '& .MuiButton-endIcon': { color: '#e94e1a', ml: '10px' }, '& .MuiSvgIcon-root': { fontSize: '18px' },
});
const calendarPopoverSx = (picker) => ({
  position: 'absolute', top: 'calc(100% + 10px)', left: picker === 'end' ? 'auto' : 0, right: picker === 'end' ? 0 : 'auto',
  zIndex: 50, width: '308px', boxSizing: 'border-box', borderRadius: '16px', border: '1px solid rgba(233,78,26,.45)',
  background: '#0d1a2c', boxShadow: '0 18px 50px rgba(0,0,0,.55)', p: '16px',
  '@media (max-width: 600px)': { left: 0, right: 'auto', width: '100%' },
});
const calendarPopoverHeaderSx = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '10px' };
const calendarNavButtonSx = {
  width: '30px', height: '30px', border: '1px solid #2a3a50', borderRadius: '9px', color: '#c6d2e2', p: 0,
  '&:hover': { borderColor: '#e94e1a', background: 'rgba(233,78,26,.08)' }, '& .MuiSvgIcon-root': { fontSize: '19px' },
};
const calendarTitleButtonSx = {
  minWidth: 0, px: '12px', py: '4px', borderRadius: '8px', border: '1px solid transparent', color: '#dfe7f1',
  fontSize: '14px', fontWeight: 700, textTransform: 'none', '&:hover': { borderColor: 'rgba(233,78,26,.5)', color: '#ffb184' },
};
const calendarPickerLabelSx = { fontSize: '10.5px', fontWeight: 700, letterSpacing: '1.5px', color: '#9aaabf', my: '8px', textTransform: 'uppercase' };
const calendarOptionGridSx = { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' };
const calendarOptionSx = (active) => ({
  minWidth: 0, py: '9px', borderRadius: '9px', border: `1px solid ${active ? '#e94e1a' : '#24344a'}`,
  background: active ? '#e94e1a' : '#0a1422', color: active ? '#0a1422' : '#dfe7f1', fontSize: '13px',
  fontWeight: active ? 700 : 400, textTransform: 'none', '&:hover': { borderColor: '#e94e1a', background: active ? '#e94e1a' : 'rgba(233,78,26,.08)' },
});
const calendarWeekdaysSx = {
  display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', mb: '4px',
  '& .MuiTypography-root': { textAlign: 'center', fontSize: '10px', fontWeight: 700, letterSpacing: '1px', color: '#e94e1a', textTransform: 'uppercase' },
};
const calendarDaysGridSx = { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' };
const calendarDaySx = ({ isCurrentMonth, isSelected, isAnchor, isInRange, isToday }) => ({
  minWidth: 0, width: '100%', height: '32px', p: 0, borderRadius: isInRange ? 0 : '8px', fontSize: '12.5px',
  color: isSelected ? '#0a1422' : isAnchor ? '#e94e1a' : isCurrentMonth ? '#dfe7f1' : '#4d5e76',
  background: isSelected ? '#e94e1a' : isInRange || isAnchor ? 'rgba(233,78,26,.16)' : 'transparent',
  fontWeight: isSelected || isAnchor ? 700 : 400, textTransform: 'none',
  boxShadow: !isSelected && isToday ? 'inset 0 0 0 1px rgba(233,78,26,.55)' : 'none',
  '&:hover': { background: isSelected ? '#e94e1a' : 'rgba(233,78,26,.16)' },
  '&.Mui-disabled': { color: '#4d5e76', opacity: 0.25 },
});
const calendarFooterSx = { borderTop: '1px solid #1d2c40', mt: '12px', pt: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' };
const calendarHintSx = { fontSize: '11.5px', color: '#9aaabf', lineHeight: 1.3 };
const calendarFooterActionsSx = { display: 'flex', gap: '8px', flex: 'none' };
const calendarCancelButtonSx = { minWidth: 0, px: '14px', py: '8px', border: '1px solid #2a3a50', borderRadius: '10px', color: '#c6d2e2', fontSize: '12.5px', textTransform: 'none' };
const calendarApplyButtonSx = {
  minWidth: 0, px: '16px', py: '8px', borderRadius: '10px', color: '#fff', fontSize: '12.5px', fontWeight: 700,
  background: 'linear-gradient(180deg,#ff7a33,#f2601a)', textTransform: 'none',
  '&:hover': { filter: 'brightness(1.08)' }, '&.Mui-disabled': { background: '#1d2c40', color: '#5c6d84' },
};
const quickRangeAreaSx = { mt: '20px' };
const quickRangeLabelSx = { fontSize: '11px', fontWeight: 700, letterSpacing: '1.5px', color: '#9aaabf', textTransform: 'uppercase' };
const quickRangeButtonsSx = { display: 'flex', gap: '10px', flexWrap: 'wrap', mt: '10px' };
const quickRangeButtonSx = {
  minWidth: 0, px: '15px', py: '8px', border: '1px solid rgba(233,78,26,.24)', borderRadius: '999px',
  color: '#dfe7f1', background: 'rgba(7,14,25,.5)', fontSize: '12.5px', lineHeight: 1.2, textTransform: 'none',
  '&:hover': { borderColor: '#e94e1a', color: '#ffb184', background: 'rgba(233,78,26,.08)' },
};
const daysRangeEditorSx = {
  display: 'flex', alignItems: 'center', gap: '10px', mt: '14px', flexWrap: 'wrap', color: '#9aaabf',
  '& .MuiTypography-root': { fontSize: '13px' },
};
const daysRangeInputSx = {
  width: '72px', '& .MuiOutlinedInput-root': { height: '38px', borderRadius: '10px', background: 'rgba(7,14,25,.5)', color: '#e8eef6' },
  '& input': { p: '8px', textAlign: 'center', fontSize: '14px' }, '& fieldset': { borderColor: '#33445c' },
};
const daysRangeApplySx = {
  minWidth: 0, px: '18px', py: '9px', border: '1px solid #e94e1a', borderRadius: '10px', background: 'rgba(233,78,26,.12)',
  color: '#ffb184', fontSize: '12.5px', fontWeight: 700, textTransform: 'none', '&:hover': { background: 'rgba(233,78,26,.22)' },
};
const rangeHelpSx = { mt: '16px', display: 'flex', alignItems: 'center', gap: '8px' };
const rangeHelpDotSx = { width: '6px', height: '6px', borderRadius: '50%', background: '#e94e1a', flex: 'none' };
const rangeHelpTextSx = { fontSize: '12.5px', color: '#9aaabf' };
const headerRowCompactSx = { ...headerRowSx, gap: '12px' };
// const calendarPanelSx = {
//   mt: '0.7vw',
//   borderRadius: '0.95vw',
//   border: '0.01vw solid rgba(233,78,26,0.18)',
//   background: 'rgba(0,0,0,0.12)',
//   overflow: 'hidden',
//   p: '0.7vw',
//   flex: 1,
//   minHeight: 0,
// };
// const formLayoutSx = { mt: '1vw', display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: '1vw' };
const configFormLayoutSx = { mt: '18px', display: 'grid', gridTemplateColumns: '1fr', gap: '20px', flex: 1, minHeight: 0 };
const formBlockSx = { borderRadius: 0, border: 0, borderTop: '1px solid #1d2c40', pt: '18px', minWidth: 0, '&:first-of-type': { borderTop: 0, pt: 0 } };
const blockTitleSx = { fontSize: '15px', fontWeight: 700, color: '#f4f7fb', lineHeight: 1.15 };
const blockTextSx = { mt: '4px', fontSize: '12.5px', color: '#95a7bb', lineHeight: 1.4 };
const toggleBoxSx = { mt: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap', px: 0, py: 0 };
const toggleLegendSx = { fontSize: '11px', color: '#c6d2e2', textTransform: 'uppercase', letterSpacing: '1.5px', fontWeight: 700 };
const toggleLabelSx = (active) => ({ fontSize: '12.5px', color: active ? '#e94e1a' : '#b6c2cf', fontWeight: 700 });
// const fieldsGridSx = { mt: '1vw', display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: '0.9vw' };
const compactFieldsGridSx = {
  mt: '14px',
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
  gap: '14px',
};
const formControlLabelSx = { m: 0, '& .MuiFormControlLabel-label': { color: '#d8e0ea', fontSize: '12px', fontWeight: 600 } };
const noticeBoxSx = { mt: '14px', borderRadius: '11px', border: '1px dashed rgba(233,78,26,0.22)', background: 'rgba(255,255,255,0.025)', px: '14px', py: '10px' };
const noticeTitleSx = { fontSize: '12px', fontWeight: 700, color: '#f2f5f8' };
const noticeTextSx = { mt: '3px', fontSize: '11.5px', color: '#97a6b7', lineHeight: 1.35 };
const compactSwitchSx = {
  width: '34px',
  height: '18px',
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: '3px',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': { backgroundColor: '#e94e1a', opacity: 1 },
    },
  },
  '& .MuiSwitch-thumb': { width: '12px', height: '12px' },
  '& .MuiSwitch-track': { borderRadius: '999px', backgroundColor: 'rgba(200,200,200,0.3)', opacity: 1 },
};
const primaryActionSx = {
  fontSize: '15px',
  fontWeight: 700,
  minWidth: '190px',
  minHeight: '46px',
  borderRadius: '12px',
  textTransform: 'none',
  color: '#fff',
  background: 'linear-gradient(135deg, #ff6a2f, #e84e0e)',
  boxShadow: '0 8px 26px rgba(232, 78, 14, 0.24)',
  '&:hover': { background: 'linear-gradient(135deg, #ff7d4c, #f05f26)' },
};
